// Mirrors docs/API.md.
import type { Language } from '@/i18n';
import type { Code, Material, Surface } from '@/utils/dental';

/** A label only: every role may do everything (DENTIST = doctor, ASSISTANT = nurse). */
export type Role = 'ADMIN' | 'DENTIST' | 'ASSISTANT';

export type UserRef = { id: string; name: string };

export type User = {
  id: string;
  name: string;
  email: string;
  role: Role;
  /** Offered as a doctor: every dentist, and an admin who also treats patients (#20). */
  treatsPatients: boolean;
  active: boolean;
  /** A platform administrator: manages every clinic, belongs to none. */
  platform: boolean;
  /** A platform administrator working in `clinic` (support mode): its settings and users only. */
  support: boolean;
  /** A nurse's doctors; empty for everyone else. */
  doctorIds: string[];
  /** The user's clinic, or in support mode the clinic entered; null at the platform console. */
  clinic: { id: string; name: string } | null;
  /** UI language chosen by the user (applied on every device they sign in on). */
  locale: Language;
};

/** A user working in a clinic: one of its own, or a platform administrator in support mode. */
export type ClinicUser = User & { clinic: { id: string; name: string } };

export const inClinic = (user: User): user is ClinicUser => user.clinic !== null;

/** Opening hours of one ISO weekday (1 = Monday … 7 = Sunday), "HH:MM". */
export type WorkingDay = { day: number; start: string; end: string };

/** The clinic's own settings. */
export type Clinic = {
  id: string;
  name: string;
  address: string | null;
  pib: string | null;
  mb: string | null;
  phone: string | null;
  email: string | null;
  /** Cache key of the logo (GET /clinic/logo?v=…); null without a logo. */
  logoVersion: string | null;
  workingHours: WorkingDay[];
  calendarStart: number;
  calendarEnd: number;
  showWeekend: boolean;
};

/** A clinic of the platform, as the platform administrator sees it. */
export type ClinicSummary = {
  id: string;
  name: string;
  active: boolean;
  userCount: number;
  createdAt: string;
};

/** An ICD-10 (MKB-10) code with its Serbian name. */
export type IcdRef = { code: string; name: string };

export type Patient = {
  id: string;
  firstName: string;
  lastName: string;
  birthDate: string | null;
  phone: string | null;
  notes: string | null;
  /** Diseases (ICD-10). */
  diagnoses: IcdRef[];
  /** Keys of the predefined list (see utils/allergies) or free text. */
  allergies: string[];
  createdAt: string;
  lastVisitAt: string | null;
  pendingCount: number;
};

export type VisitStatus = 'OPEN' | 'CONFIRMED';

export type Visit = {
  id: string;
  startedAt: string;
  dentist: UserRef | null;
  /** Confirmed by entering the total price; stays editable. */
  status: VisitStatus;
  /** Total price in RSD; null until confirmed. */
  priceRsd: number | null;
  confirmedBy: UserRef | null;
  confirmedAt: string | null;
  /** "Fiskal": marked for the future e-karton XML export. */
  fiscal: boolean;
};

export type PatientDetail = Patient & { visits: Visit[] };

export type EntrySource = 'DICTATION' | 'MANUAL';
/** PENDING remains only on entries recorded before visits were confirmed as a whole. */
export type EntryStatus = 'PENDING' | 'APPROVED' | 'REJECTED';
/** Existing state found in the mouth, or work performed in the visit. */
export type EntryKind = 'EXISTING' | 'PERFORMED';

export type Entry = {
  id: string;
  clientId: string;
  patientId: string;
  visitId: string | null;
  tooth: number;
  surfaces: Surface[];
  code: Code;
  material: Material | null;
  note: string | null;
  source: EntrySource;
  status: EntryStatus;
  kind: EntryKind;
  /** The clause of the dictation this entry came from. */
  transcript: string | null;
  /** The full dictation (Transcript) the clause belongs to. */
  transcriptId: string | null;
  confidence: number | null;
  corrected: boolean;
  rejectReason: string | null;
  createdBy: UserRef;
  createdAt: string;
  approvedBy: UserRef | null;
  approvedAt: string | null;
  /** Set on the device for entries still waiting in the offline queue. */
  local?: boolean;
};

export type NewEntry = {
  clientId: string;
  tooth: number;
  surfaces: Surface[];
  code: Code;
  material?: Material | null;
  note?: string | null;
  source: EntrySource;
  /** Findings are always EXISTING; a procedure defaults to PERFORMED. */
  kind?: EntryKind;
  transcript?: string | null;
  /** clientId of the dictation the clause came from. */
  transcriptClientId?: string;
  confidence?: number | null;
  recordedAt?: string;
};

/** Everything said or typed in one dictation session, in the language it was dictated in. */
export type Transcript = {
  id: string;
  clientId: string;
  patientId: string;
  visitId: string | null;
  /** BCP 47 speech language (sr-RS, hu-HU…); '' until the device has sent it. */
  lang: string;
  text: string;
  createdBy: UserRef;
  createdAt: string;
  editedBy: UserRef | null;
  editedAt: string | null;
  /** Set on the device while the latest text still waits to be sent. */
  local?: boolean;
};

export type EntryPatch = Partial<
  Pick<Entry, 'tooth' | 'surfaces' | 'code' | 'material' | 'note' | 'kind'>
>;

export type AuditEvent = {
  id: string;
  action: string;
  entityType: string;
  entityId: string | null;
  user: UserRef | null;
  data: unknown;
  createdAt: string;
};

/** An appointment in the clinic calendar (never creates a patient). */
export type Booking = {
  id: string;
  startsAt: string;
  durationMin: number;
  name: string;
  phone: string | null;
  /** Set when the name was picked from the patient list. */
  patient: { id: string; name: string } | null;
  dentist: UserRef | null;
  createdBy: UserRef;
};

/** An X-ray image of a visit; the file is GET /xrays/:id/image (needs the auth header). */
export type Xray = {
  id: string;
  clientId: string;
  patientId: string;
  visitId: string | null;
  /** FDI tooth numbers on the image; empty when not tagged. */
  teeth: number[];
  width: number;
  height: number;
  bytes: number;
  takenAt: string;
  createdBy: UserRef;
  createdAt: string;
  /** Still on this device, waiting to be sent. */
  local?: boolean;
  /** The server refused it (only for `local`). */
  failed?: string;
};

/** A treatment plan offer (no statuses; valid 60 days from issue). */
export type Offer = {
  id: string;
  /** "2026-001": year and sequence, per clinic. */
  number: string;
  /** Set when the name was matched in the patient list. */
  patient: { id: string; name: string } | null;
  patientName: string;
  steps: string[];
  totalRsd: number;
  note: string | null;
  issuedAt: string;
  /** YYYY-MM-DD. */
  validUntil: string;
  doctor: UserRef | null;
  createdBy: UserRef;
};

/** GET /reports/summary: revenue of confirmed visits and the work done. */
export type ReportSummary = {
  year: number;
  /** The month the work types are for; null = the whole year. */
  month: number | null;
  months: { month: number; revenueRsd: number; visits: number }[];
  totalRsd: number;
  visits: number;
  years: { year: number; revenueRsd: number; visits: number }[];
  /** Entry code of work performed; null = confirmed visits without any. */
  workTypes: { code: string | null; count: number; revenueRsd: number }[];
};

export type ApiError = { code: string; message: string; details?: unknown };
